import { API } from '@/modules/api'
import { defineStore } from 'pinia'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import type { SkywardFee } from '@/modules/api/get/skywardFees.ts'
import type { TSkywardFeeCartLine } from '@/types/cart.ts'
import { useCartStore } from '@/stores/cart'

export const createUniqueId = (feeId: number) => feeId + '::' + OrderItemTypeCode.SkywardFee

export const useSkywardFeeStore = defineStore('skywardFee', {
  state: () => ({
    isLoaded: {} as Record<TStudentAccountId, boolean>,
    fines: {} as Record<TStudentAccountId, SkywardFee[]>,
    currentRequestController: null as AbortController | null
  }),

  getters: {
    isLoading(state) {
      return (studentAccountId: TStudentAccountId) => !(state.isLoaded[studentAccountId] ?? false)
    },
    getFines(state) {
      return (studentAccountId: TStudentAccountId) => state.fines[studentAccountId] ?? []
    }
  },

  actions: {
    async fetch(_districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      this.isLoaded[studentAccountId] = false
      // Cancel any existing request
      if (this.currentRequestController) {
        this.currentRequestController.abort()
      }

      // Create new controller for this request
      this.currentRequestController = new AbortController()

      try {
        this.fines[studentAccountId] = await API.Get.skywardFees(studentAccountId, {
          signal: this.currentRequestController.signal
        })
        this.validateCart(_districtId, studentAccountId)
        this.isLoaded[studentAccountId] = true
      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.log('Skyward fees fetch was cancelled')
          return
        }
        throw error
      } finally {
        this.currentRequestController = null
      }
    },

    // This function is used to validate the cart lines for the skyward fee store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      const cartStore = useCartStore()
      for (const line of cartStore.getCartLines(districtId) as TSkywardFeeCartLine[]) {
        if (
          line.orderItemType === OrderItemType.SkywardFee &&
          line.studentAccountId === studentAccountId
        ) {
          const fee = this.fines[studentAccountId].find(fee => fee.feeId === line.feeId)
          if (!fee) {
            console.log('skyward fee not found, removing from cart', line.feeId)
            this.removeFromCart(districtId, line.feeId)
          }
          // ensure fee amount is less than cart amount meaning cart amount is too high
          if (fee && fee.amountDue < line.amount) {
            console.log('skyward fee amount is less than cart amount, adjusting cart', line.feeId)
            cartStore.updateCartLine(districtId, line.uniqueId, {
              ...line,
              amount: fee.amountDue
            })
          }
        }
      }
    },

    addToCart(
      districtId: TDistrictId,
      feeData: Omit<TSkywardFeeCartLine, 'orderItemType' | 'uniqueId'>
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)

      if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, feeData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, {
          ...feeData,
          orderItemType: OrderItemType.SkywardFee,
          uniqueId
        })
      }
    },

    updateCartLine(
      districtId: TDistrictId,
      feeData: Partial<TSkywardFeeCartLine> & { feeId: number }
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      cartStore.updateCartLine(districtId, uniqueId, feeData)
    },

    removeFromCart(districtId: TDistrictId, feeId: number) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeId)
      cartStore.removeCartLine(districtId, uniqueId)
    }
  }
})
